// Per-mode rules. Frozen tuning — runtime systems read but never mutate. The host
// reads MatchConfig at start() and bakes the relevant fields onto MatchState. Adding
// a field here means deciding whether it lives on MatchState (because systems need
// it per-tick) or stays config-only (because only the host/UI consult it).
import type { MapId } from './maps/index.js';

export interface MatchConfig {
  matchLengthSec: number;
  squadCap: number;
  // V1.5: enables the mid-relay neutral camp (CampSystem). Optional; defaults
  // to false in `createInitialState` so V0.x and tests using inline configs
  // are unaffected. The shipped V1.5 config below sets it true.
  enableNeutralCamps?: boolean;
  // V1.5: enables the Signal Link layer (SignalLinkSystem) per
  // docs/02_GAME_DESIGN.md §11 Option A. Optional; defaults to false. When
  // off, every owned relay reads `isConnected: true` so EconomySystem and
  // FogSystem behave exactly as in V0.x. The V1.5 config below sets it true.
  enableSignalLinks?: boolean;
  // V1.5: which polished map to load. Optional; defaults to 'foundry' in
  // `createInitialState`. The MAPS registry in data/maps/index.ts owns the
  // id → MapDef lookup.
  mapId?: MapId;
}

// V0.3: 5-minute matches, 4 squads per side. Retained for tests that still want a
// short match. Neutral camps off — V0.3 predates them. mapId pinned to 'foundry'
// because every test built on this config asserts against Foundry's 3-relay
// geometry (north/mid/south at x=1000). After the player-facing default flipped
// to Crucible, these legacy configs keep their original map so tests stay green.
export const MATCH_CONFIG_V0_3: MatchConfig = Object.freeze({
  matchLengthSec: 300,
  squadCap: 4,
  enableNeutralCamps: false,
  mapId: 'foundry',
});

// V0.4: 8-minute matches per docs/prompts/04_production_buildings.md. Squad cap
// is 6 (docs/02_GAME_DESIGN.md §2 — "V0.4+: cap = 6"). Neutral camps off —
// V0.4 predates them; the V1.5 host opts in via MATCH_CONFIG_V1_5. mapId pinned
// to 'foundry' — same rationale as MATCH_CONFIG_V0_3 above; this is also the
// default arg of `createInitialState`, so bare `new Simulation(seed)` calls in
// tests still get Foundry.
export const MATCH_CONFIG_V0_4: MatchConfig = Object.freeze({
  matchLengthSec: 480,
  squadCap: 6,
  enableNeutralCamps: false,
  mapId: 'foundry',
});

// V1.5: same cap as V0.4 but neutral camps + signal links on.
// Squad cap = 6 per docs/02_GAME_DESIGN.md §2 — six squads on the V1.5+
// Crucible 7-relay map gives players room to split forces across multiple
// lanes without raw mass blow-out (Layer-6 vulnerability per §4.6 picks up
// the slack).
//
// V1.6 readability pass — neutral camps disabled after playtest. The green
// turret + drones added too much visual noise without clearly earning their
// keep onboarding-wise. CampSystem + data/camps.ts remain in the codebase
// gated by this flag, so a future redesign can re-enable them.
//
// V2 Tempo patch (2026-05-24) — match length 480 → 360 (8:00 → 6:00).
// Pairs with the Signal Surge move (5:30 → 2:30) and Signal Storm move
// (7:00 → 5:00) to give the match a tight 3-beat arc that lands its
// climaxes BEFORE the historical 3m09s quit point. Shorter matches also
// mean more matches per session, which is the actual avg-playtime
// pressure point (the KPI sums across matches, not per-match).
//
// mapId pinned to 'foundry' — the V1.5 integration suite (telemetry,
// forwardTurret*, signalLinks, neutralCamp, etc.) hard-codes Foundry relay
// coordinates. The shipped player-facing default lives in
// apps/client/src/game/MatchConfig.ts (MATCH_CONFIG_DEFAULT) and explicitly
// overrides mapId to 'crucible'; the server room does the same.
export const MATCH_CONFIG_V1_5: MatchConfig = Object.freeze({
  matchLengthSec: 360,
  squadCap: 6,
  enableNeutralCamps: false,
  enableSignalLinks: true,
  mapId: 'foundry',
});

// V1.5 spine variant that keeps the camp opt-in switched on. Used only by
// CampSystem / camp telemetry tests — production play and the integration
// suite read MATCH_CONFIG_V1_5 above, which has camps off. matchLengthSec
// stays at 480 here because the camp tests assert on the older pacing
// schedule and are scoped to V1.5 specifically.
export const MATCH_CONFIG_V1_5_CAMPS: MatchConfig = Object.freeze({
  matchLengthSec: 480,
  squadCap: 6,
  enableNeutralCamps: true,
  enableSignalLinks: true,
  mapId: 'foundry',
});
